/**
 * Polling that stops when nobody is looking (US-18.14).
 *
 * <p>A badge that asks the server every minute costs one request per open tab per minute, for ever,
 * whether or not anybody is in front of it — and that is traffic which grows with the number of
 * people signed in rather than with the work they do. Two people who leave the application open in a
 * background tab overnight are eight hundred requests nobody reads.
 *
 * <p>So: the interval is a function of the document's visibility, checked each time the timer would
 * fire, and a hidden tab answers `false` — the timer stops. Coming back is not a wait, because
 * `refetchOnWindowFocus` fetches immediately when the tab becomes visible again. React Query's own
 * `refetchIntervalInBackground: false` (the default) already pauses on blur; this is the same rule
 * said out loud, and it also covers the case where the tab is hidden without the window losing focus.
 *
 * <p>Not SSE, and not a websocket: this is one integer, and a connection per signed-in person to
 * carry it would be the only long-lived connection in the application (decision 16 of EP-18).
 */
export function pollWhileVisible(intervalMs: number) {
  return {
    refetchInterval: () =>
      typeof document === "undefined" || document.visibilityState === "visible" ? intervalMs : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  } as const;
}
